import {
  ANALYTICS,
  ANALYTICS_UNDO_SALE,
  BENEFICIARIES,
  COLLECT_PAYMENT,
  DASHBOARD,
  INVENTORY,
  OUTLET_MANAGEMENT,
  POS_MANAGEMENT,
  SEND_MONEY,
  STATEMENTS,
  TEAM_MANAGEMENT,
  TRANSACTIONS,
  VIRTUAL_ACCOUNTS,
} from "@zenith/routes/paths";

import type { Permission } from "./permissions";

/**
 * What a route needs, so a page a role may not use cannot be opened by typing
 * its URL. Hiding a menu item is not access control on its own.
 *
 * A route lists every permission that satisfies it - Inventory, for instance,
 * is open to a Manager who may only change stock as well as to the owner who
 * may create products - and a guard needs just one of them.
 */
const ROUTE_PERMISSIONS: Array<{ prefix: string; anyOf: Permission[] }> = [
  { prefix: COLLECT_PAYMENT, anyOf: ["payment:collect"] },
  { prefix: SEND_MONEY, anyOf: ["transfer:initiate"] },
  { prefix: BENEFICIARIES, anyOf: ["transfer:initiate"] },
  { prefix: VIRTUAL_ACCOUNTS, anyOf: ["vaas:manage"] },
  { prefix: POS_MANAGEMENT, anyOf: ["pos:manage"] },
  { prefix: TRANSACTIONS, anyOf: ["transactions:view"] },
  { prefix: ANALYTICS_UNDO_SALE, anyOf: ["sale:undo"] },
  { prefix: ANALYTICS, anyOf: ["analytics:view"] },
  { prefix: INVENTORY, anyOf: ["inventory:manage", "inventory:stock"] },
  { prefix: OUTLET_MANAGEMENT, anyOf: ["outlet:read"] },
  { prefix: TEAM_MANAGEMENT, anyOf: ["team:manage"] },
  { prefix: STATEMENTS, anyOf: ["settings:statement"] },
  { prefix: DASHBOARD, anyOf: ["home:view"] },
];

/** The permissions that satisfy `pathname`, or none when it is unrestricted. */
export const permissionsForPath = (pathname: string): Permission[] => {
  // Longest prefix wins, so /analytics/undo-sale is not read as plain analytics.
  const match = ROUTE_PERMISSIONS.filter(
    (route) =>
      pathname === route.prefix || pathname.startsWith(`${route.prefix}/`)
  ).sort((a, b) => b.prefix.length - a.prefix.length)[0];

  return match?.anyOf ?? [];
};
